import React from 'react';
import {
	FormattedMessage,
} from 'react-intl';

import type CK from '~/types/contentking';

import AccountLink from '~/components/app/AccountLink';
import PlaceholderLink from '~/components/app/PlaceholderLink';
import WebsiteSettingsLink from '~/components/app/WebsiteSettingsLink';
import MenuItemLayout from '~/components/atoms/navigations/builders/MenuItemLayout';
import NavigationIcon, {
	NavigationIconType,
} from '~/components/patterns/icons/NavigationIcon';

import {
	type messages
} from '../MainNavigation';



type Props = {
	accountId: CK.AccountId | null,
	collapseAllExpandables?: () => void,
	effectiveHomeAccountId: CK.AccountId | null,
	isAnyExpandableExpanded?: boolean,
	messages: typeof messages,
	websiteId: CK.WebsiteId | null,
};

/**
 * Build secondary navigation items (Integrations, Settings, Account)
 *
 * Returns an array of JSX elements for the secondary navigation section
 */
export function buildNewSecondaryItems(props: Props): Array<React.ReactElement> {
	const {
		accountId,
		collapseAllExpandables,
		effectiveHomeAccountId,
		isAnyExpandableExpanded = false,
		messages,
		websiteId,
	} = props;

	return [
		// Integrations
		<li
			className="main-nav__item"
			data-secondary-nav-item="true"
			key="integrations-item"
		>
			<PlaceholderLink
				onClick={collapseAllExpandables}
				sectionName="integrations"
			>
				{({ isActive, isDimmed }) => {
					return (
						<MenuItemLayout
							icon={(
								<NavigationIcon
									size={20}
									type={NavigationIconType.Integrations}
								/>
							)}
							isActive={isActive && !isAnyExpandableExpanded}
							isDimmed={isDimmed}
							isLabelHidden={false}
							label={(
								<FormattedMessage {...messages.integrations} />
							)}
						/>
					);
				}}
			</PlaceholderLink>
		</li>,
		// Settings
		<li
			className="main-nav__item"
			data-secondary-nav-item="true"
			key="settings-item"
		>
			<WebsiteSettingsLink
				onClick={collapseAllExpandables}
				websiteId={websiteId}
			>
				{({ isActive, isDimmed }) => (
					<MenuItemLayout
						icon={(
							<NavigationIcon
								size={20}
								type={NavigationIconType.Settings}
							/>
						)}
						isActive={isActive && !isAnyExpandableExpanded}
						isDimmed={isDimmed}
						isLabelHidden={false}
						label={(
							<FormattedMessage {...messages.settings} />
						)}
					/>
				)}
			</WebsiteSettingsLink>
		</li>,
		// Account (per clickdummy structure)
		<li
			className="main-nav__item"
			data-secondary-nav-item="true"
			key="account-item"
		>
			<AccountLink
				accountId={accountId ?? effectiveHomeAccountId ?? undefined}
				onClick={collapseAllExpandables}
			>
				{({ isActive, isDimmed }) => (
					<MenuItemLayout
						icon={(
							<NavigationIcon
								size={20}
								type={NavigationIconType.Account}
							/>
						)}
						isActive={isActive && !isAnyExpandableExpanded}
						isDimmed={isDimmed}
						isLabelHidden={false}
						label={(
							<FormattedMessage {...messages.account} />
						)}
					/>
				)}
			</AccountLink>
		</li>,
	];
}
